import { useCallback, useEffect, useRef, useState } from "react";
import type { DemoMode, InboxDemoSequence } from "../data/types";

type ControllerActions = {
  selectConversation: (conversationId: string) => void;
  setDraft: (text: string) => void;
  sendMessage: (text?: string) => void | Promise<void>;
  scrollMessages: (position: "top" | "bottom") => void;
};

type UseInboxDemoControllerArgs = {
  sequence?: InboxDemoSequence;
  actions: ControllerActions;
};

export function useInboxDemoController({
  sequence,
  actions,
}: UseInboxDemoControllerArgs) {
  const [mode, setMode] = useState<DemoMode>(
    sequence && sequence.length > 0 ? "auto" : "interactive",
  );
  const timersRef = useRef<number[]>([]);
  const cancelledRef = useRef(false);
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const wait = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timersRef.current.push(id);
    });
  }, []);

  const stopAutoDemo = useCallback(() => {
    cancelledRef.current = true;
    clearTimers();
    setMode("interactive");
  }, [clearTimers]);

  const handleUserIntent = useCallback(() => {
    if (cancelledRef.current && mode === "interactive") return;
    if (mode === "auto") {
      stopAutoDemo();
    }
  }, [mode, stopAutoDemo]);

  useEffect(() => {
    if (!sequence?.length) {
      setMode("interactive");
      return;
    }

    cancelledRef.current = false;
    setMode("auto");

    const run = async () => {
      for (const step of sequence) {
        if (cancelledRef.current) return;
        await wait(step.delay ?? 700);
        if (cancelledRef.current) return;

        if (step.type === "selectConversation") {
          actionsRef.current.selectConversation(step.conversationId);
        } else if (step.type === "type") {
          actionsRef.current.setDraft("");
          for (let index = 1; index <= step.text.length; index += 1) {
            if (cancelledRef.current) return;
            actionsRef.current.setDraft(step.text.slice(0, index));
            await wait(28);
          }
        } else if (step.type === "send") {
          await actionsRef.current.sendMessage();
        } else if (step.type === "scrollMessages") {
          actionsRef.current.scrollMessages(step.position);
        }
      }

      if (!cancelledRef.current) {
        setMode("interactive");
      }
    };

    void run();

    return () => {
      cancelledRef.current = true;
      clearTimers();
    };
  }, [clearTimers, sequence, wait]);

  return {
    mode,
    stopAutoDemo,
    handleUserIntent,
  };
}
